// @flow strict

import * as React from 'react'
import { Formik, Field, Form, type FormikProps, ErrorMessage } from 'formik'
import { type FormState, type FormResult } from './formTypes'

import {
  formStateToMoreInformationRequest,
  businessMoreInformationFormSchema,
} from './formUtils'
import styles from './MoreInformationForm.module.scss'
import { BusinessButton } from '@mondough/public-web-components'

const businessTypes = {
  'sole-trader': 'Sole Trader',
  'limited-company': 'Limited Company (LTD)',
}

const emptyInitialValues: FormState = {
  userName: '',
  userEmail: '',
  businessType: 'default',
  businessName: '',
  companiesHouseRegistrationNumber: '',
  businessIndustry: 'default',
  message: '',
  privacy: false,
}

const BASE_URL = process.env.GATSBY_API_BASE_PATH
const OAUTH_ID = process.env.GATSBY_OAUTH_ID
const OAUTH_SECRET = process.env.GATSBY_OAUTH_SECRET

const submitForm = (values, setIsLoading, setResult) => {
  setIsLoading(true)

  fetch(`${BASE_URL}/oauth2/token`, {
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: OAUTH_ID,
      client_secret: OAUTH_SECRET,
    }),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: 'application/json',
    },
  })
    .then(async result => {
      const { access_token } = await result.json()
      fetch(`${BASE_URL}/business-banking-waitlist`, {
        method: 'POST',
        body: JSON.stringify(formStateToMoreInformationRequest(values)),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access_token}`,
        },
      })
        .then(async => {
          setResult({
            success: true,
            message: 'Thanks! Someone from the team will contact you soon.',
          })
          setIsLoading(false)
        })
        .catch(err => {
          setIsLoading(false)
          setResult({
            success: false,
            message: 'Sorry, that didn’t work – please try again later!',
          })
          console.error(err)
        })
    })
    .catch(err => {
      setIsLoading(false)
      setResult({
        success: false,
        message: 'Sorry, that didn’t work – please try again later!',
      })
      console.error(err)
    })
}

const MoreInformationForm = ({
  setIsLoading,
  isLoading,
  setResult,
  businessIndustries,
}: {
  setIsLoading(boolean): void,
  isLoading: boolean,
  setResult(FormResult): void,
  // $FlowIgnore
  businessIndustries: { [key: string]: any },
}) => (
  <Formik
    initialValues={emptyInitialValues}
    validationSchema={businessMoreInformationFormSchema}
    onSubmit={values => submitForm(values, setIsLoading, setResult)}
    validateOnChange={false}
    validateOnBlur={false}
  >
    {({
      handleSubmit,
      handleReset,
      submitCount,
      errors,
      values,
      ...props
    }: FormikProps<FormState>) => (
      <Form className={styles.form}>
        <Field name="userName">
          {({ field, form }) => (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={field.name}>
                What is your name?
              </label>
              <input {...field} type="text" id={field.name} />
              {field.name && errors[field.name] && (
                <div className={styles.errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>

        <Field name="userEmail">
          {({ field, form }) => (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={field.name}>
                What is your email address?
                <span className={styles.subLabel}>
                  If you have a Monzo account, please use that email address
                </span>
              </label>
              <input
                {...field}
                type="text"
                id={field.name}
                placeholder="you@example.com"
              />
              {field.name && errors[field.name] && (
                <div className={styles.errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>

        <Field name="businessType">
          {({ field, form }) => (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={field.name}>
                What type of business is it?
                <span className={styles.subLabel}>
                  We currently only support business types in this list
                </span>
              </label>
              <select {...field} id={field.name}>
                <option value="default" disabled={true}>
                  Select One
                </option>
                {Object.keys(businessTypes).map(key => {
                  return (
                    <option value={key} key={key}>
                      {businessTypes[key]}
                    </option>
                  )
                })}
              </select>
              {field.name && errors[field.name] && (
                <div className={styles.errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>

        {values.businessType !== 'limited-company' && (
          <Field name="businessName">
            {({ field, form }) => (
              <div className={styles.field}>
                <label className={styles.label} htmlFor={field.name}>
                  What is your business name?
                </label>
                <input {...field} type="text" id={field.name} />
                {field.name && errors[field.name] && (
                  <div className={styles.errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
        )}

        {values.businessType === 'limited-company' && (
          <Field name="companiesHouseRegistrationNumber">
            {({ field, form }) => (
              <div className={styles.field}>
                <label className={styles.label} htmlFor={field.name}>
                  Companies house registration number
                </label>
                <input {...field} type="text" id={field.name} />
                {field.name && errors[field.name] && (
                  <div className={styles.errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
        )}

        {values.businessType !== 'limited-company' && (
          <Field name="businessIndustry">
            {({ field, form }) => (
              <div className={styles.field}>
                <label className={styles.label} htmlFor={field.name}>
                  Which industry sounds most like your business?
                </label>
                <select {...field} id={field.name}>
                  <option value="default" disabled={true}>
                    Select One
                  </option>
                  {businessIndustries.map(industry => (
                    <option value={industry} key={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
                {field.name && errors[field.name] && (
                  <div className={styles.errorMessage}>
                    <ErrorMessage name={field.name} />
                  </div>
                )}
              </div>
            )}
          </Field>
        )}

        <Field name="message">
          {({ field, form }) => (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={field.name}>
                What would you like to know more about?
                <span className={styles.subLabel}>
                  We'll get back to you as soon as we can
                </span>
              </label>
              <textarea {...field} id={field.name} />
              {field.name && errors[field.name] && (
                <div className={styles.errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>

        <Field name="privacy">
          {({ field, form }) => (
            <div className={styles.field}>
              <div className={styles.checkbox}>
                <input {...field} id="privacy" type="checkbox" value={false} />
                <label
                  className={styles.customOptionFieldLabel}
                  htmlFor={field.name}
                >
                  I agree that Monzo can use my information to contact me about
                  Monzo Business.
                  <span className={styles.subLabel}>
                    Please see our{' '}
                    <a
                      className={styles.privacyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://monzo.com/legal/business-account-privacy-notice/"
                    >
                      Business Privacy Notice
                    </a>{' '}
                    for more information on how we store and use your data.
                  </span>
                </label>
              </div>
              {field.name && errors[field.name] && (
                <div className={styles.errorMessage}>
                  <ErrorMessage name={field.name} />
                </div>
              )}
            </div>
          )}
        </Field>

        <BusinessButton type="submit" theme="primary" disabled={isLoading}>
          Submit form
        </BusinessButton>
        {errors && Object.keys(errors).length ? (
          <div className={styles.errorMessage}>Please fix the errors above</div>
        ) : null}
      </Form>
    )}
  </Formik>
)

export default MoreInformationForm
